'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDeviceType } from './DeviceTypeProvider';

export type BreakpointConfig = Partial<
  Record<'sm' | 'md' | 'lg' | 'xl' | 'xl2', string>
> & { base?: string };

const BREAKPOINT_PRIORITY = ['xl2', 'xl', 'lg', 'md', 'sm'] as const;

export type GetClassValueOptions = {
  /** Fallback classes used during SSR / before first measurement. */
  defaultClasses: string;
  /** Declarative breakpoint → class mapping. Highest active breakpoint wins. Falls back to `base`. */
  breakpoints?: BreakpointConfig;
  /** When present, overrides `breakpoints` and receives current breakpoint values to return a class string. */
  callback?: (ctx: ContentSizeContextValue) => string;
};

type ContentSizeContextValue = {
  isReady?: boolean; // True after first measurement, false during SSR and before first measurement
  /** Width of the content container in px (0 before first measurement). */
  contentWidth: number;
  /** True when the content area is narrower than 720 px. */
  isMobile: boolean;
  /** True when the content area is between 720 and 1024 px. */
  isTablet: boolean;
  /** True when the content area is ≥ 1024 px. */
  isXLScreen: boolean;
  /** True when the content area is ≥ 1280 px. */
  isLargeScreen: boolean;

  sm: boolean;
  md: boolean;
  lg: boolean;
  xl: boolean;
  xl2: boolean;
  /**
   * Resolves a class string based on the current content-container width.
   *
   * How it works:
   * 1. Before first measurement (SSR / hydration), returns `defaultClasses` as-is.
   * Use standard Tailwind responsive prefixes (e.g. `md:`, `lg:`) here so the
   * server-rendered HTML is correct.
   * 2. After measurement, evaluates `breakpoints` from highest to lowest
   * (xl2 → xl → lg → md → sm). The first matching breakpoint's value wins.
   * These values should be **unprefixed** Tailwind classes since the breakpoint
   * logic is handled by JS, not CSS media queries.
   * 3. If no breakpoint matches, falls back to `breakpoints.base` (defaults to `''`).
   * Use `base` for classes that apply below all defined breakpoints (mobile-first).
   * 4. If `callback` is provided, it overrides `breakpoints` entirely and receives
   * the full context value for custom logic.
   * @example
   * // Declarative breakpoints with base fallback
   * getClassValue({
   *   defaultClasses: 'mr-1 md:mr-2 lg:mr-16',
   *   breakpoints: {
   *     md: 'mr-2',
   *     lg: 'mr-16',
   *     base: 'mr-1',
   *   },
   * });
   * @example
   * // Callback for custom logic
   * getClassValue({
   *   defaultClasses: 'p-2',
   *   callback: (ctx) => ctx.isMobile ? 'p-2' : 'p-8',
   * });
   */
  getClassValue: (options: GetClassValueOptions) => string;
};

const ContentSizeContext = createContext<ContentSizeContextValue | null>(null);

/**
 * Provides content-container-aware size breakpoints via ResizeObserver.
 * Pass a `containerRef` pointing to the DOM element to observe.
 */
export const ContentSizeProvider = ({
  children,
  containerRef,
}: {
  children: React.ReactNode;
  containerRef: React.RefObject<HTMLElement | null>;
}) => {
  const [contentWidth, setContentWidth] = useState(0);

  // Single effect for measurement and ResizeObserver setup
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Initial measurement
    setContentWidth(Math.round(el.getBoundingClientRect().width));

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContentWidth(Math.round(entry.contentRect.width));
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [containerRef]);

  const isMobile = contentWidth < 720;
  const isTablet = contentWidth >= 720 && contentWidth < 1024;
  const isXLScreen = contentWidth >= 1024;
  const isLargeScreen = contentWidth >= 1280;
  const sm = contentWidth >= 640;
  const md = contentWidth >= 768;
  const lg = contentWidth >= 1024;
  const xl = contentWidth >= 1280;
  const xl2 = contentWidth >= 1536;

  const value = useMemo(
    () => ({
      isReady: !!contentWidth,
      contentWidth,
      isMobile,
      isTablet,
      isXLScreen,
      isLargeScreen,
      sm,
      md,
      lg,
      xl,
      xl2,
    }),
    [
      contentWidth,
      isMobile,
      isTablet,
      isXLScreen,
      isLargeScreen,
      sm,
      md,
      lg,
      xl,
      xl2,
    ],
  );

  const getClassValue = useCallback(
    ({
      defaultClasses,
      breakpoints,
      callback,
    }: GetClassValueOptions): string => {
      if (!value.isReady) {
        return defaultClasses;
      }
      if (callback) {
        return callback(value as ContentSizeContextValue);
      }
      if (!breakpoints) return defaultClasses;
      for (const bp of BREAKPOINT_PRIORITY) {
        const classValue = breakpoints[bp];
        if (classValue !== undefined && value[bp]) {
          return classValue;
        }
      }
      return breakpoints.base ?? '';
    },
    [value],
  );

  return (
    <ContentSizeContext.Provider
      value={{
        ...value,
        getClassValue,
      }}
    >
      {children}
    </ContentSizeContext.Provider>
  );
};

/**
 * Returns breakpoints based on the content container width.
 * Falls back to mobile-first values when used outside a `ContentSizeProvider`.
 */
export const useContentSize = (
  useDeviceFallback?: boolean,
): ContentSizeContextValue => {
  const ctx = useContext(ContentSizeContext);

  const sizes = useDeviceType();

  // During SSR or before first measurement, assume mobile-first to match server
  if (!ctx || !ctx.contentWidth) {
    const getClassValue = ({ defaultClasses }: GetClassValueOptions) =>
      defaultClasses;
    return {
      isReady: false,
      contentWidth: 0,
      isMobile: true,
      isTablet: false,
      isXLScreen: false,
      isLargeScreen: false,
      sm: false,
      md: false,
      lg: false,
      xl: false,
      xl2: false,
      getClassValue,
      ...(useDeviceFallback
        ? { ...sizes, contentWidth: sizes.screenWidth }
        : {}),
    };
  }

  return ctx;
};
